import React, { useState } from 'react';
import { Warehouse, Wrench, ClipboardCheck, Truck, Package, HelpCircle } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { motion } from 'framer-motion';

// Create properly typed motion components
const MotionH1 = motion.h1;
const MotionP = motion.p;

export default function Tariff() {
  const services = [
    { icon: <Warehouse className="h-5 w-5 text-primary" />, name: 'Container Storage', desc: 'Per TEU, per day (24/7 secure yard access)' },
    { icon: <Wrench className="h-5 w-5 text-primary" />, name: 'Container Repairs', desc: 'Welding, panel, door, floor, paint (per job)' },
    { icon: <ClipboardCheck className="h-5 w-5 text-primary" />, name: 'Container Survey', desc: 'Inspection & certification (per container)' },
    { icon: <Truck className="h-5 w-5 text-primary" />, name: 'Transportation (Yard Move)', desc: 'On-site movement (per move, up to 40ft)' },
    { icon: <Package className="h-5 w-5 text-primary" />, name: 'Leasing (Empty Container)', desc: 'Per TEU, per month (minimum 1 month)' },
    { icon: <HelpCircle className="h-5 w-5 text-primary" />, name: 'Custom Services', desc: 'Bulk, project, or special requirements' },
  ];

  const faqs = [
    { q: 'Are there discounts for bulk or long-term storage?', a: 'Yes! Please contact us for custom pricing on bulk or long-term requirements.' },
    { q: 'How do I request a quote?', a: 'Click the "Request a Custom Quote" button or visit our Contact page.' },
    { q: 'Are all services available 24/7?', a: 'Most services are available round-the-clock. Please confirm for special requirements.' },
  ];
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [heroImg, setHeroImg] = useState('/assets/images/real3.jpg');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative py-24 bg-primary">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={heroImg}
            alt="Tariff Hero"
            className="w-full h-full object-cover opacity-20"
            onError={e => {
              if (e.currentTarget.src.includes('real3.jpg')) {
                e.currentTarget.src = '/assets/images/Photo2.jpg';
                setHeroImg('/assets/images/Photo2.jpg');
              } else {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1565358795042-526f1881723f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80';
                setHeroImg('https://images.unsplash.com/photo-1565358795042-526f1881723f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80');
              }
            }}
            draggable="false"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <MotionH1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Tariff & Pricing
            </MotionH1>
            <MotionP
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl"
            >
              Transparent, competitive rates for all our container services. For custom or bulk requirements, please contact us for a quote.
            </MotionP>
          </div>
        </div>
        <div className="absolute inset-0 bg-primary/80 pointer-events-none select-none" />
      </div>

      {/* Tariff Table */}
      <Section className="pt-0 pb-0">
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white dark:bg-navy-light rounded-xl">
            <thead>
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Service</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s, i) => (
                <tr
                  key={s.name}
                  className={
                    `border-t border-gray-100 dark:border-gray-800 transition-colors duration-200 ` +
                    (i % 2 === 0 ? 'bg-white dark:bg-navy-light' : 'bg-gray-50 dark:bg-navy') +
                    ' hover:bg-primary/5 dark:hover:bg-primary/10'
                  }
                >
                  <td className="px-6 py-5 font-medium text-gray-900 dark:text-white flex items-center gap-3 whitespace-nowrap">{s.icon}{s.name}</td>
                  <td className="px-6 py-5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{s.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* CTA */}
      <Section className="pt-0 pb-0">
        <div className="max-w-xl mx-auto bg-primary/95 dark:bg-primary/80 rounded-xl px-8 py-10 text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Need a Custom Quote?</h2>
          <p className="text-white/80 mb-6">Let us know your requirements and we'll get back to you with a tailored offer.</p>
          <a href="/contact" className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg shadow hover:bg-gray-100 transition-all duration-200">Request a Custom Quote</a>
        </div>
      </Section>

      {/* FAQ Section */}
      <Section title="Frequently Asked Questions" className="pt-0">
        <div className="max-w-2xl mx-auto">
          {faqs.map((faq, i) => (
            <div key={i} className="border-b border-gray-200 dark:border-gray-700 last:border-0">
              <button
                className="w-full text-left py-5 focus:outline-none flex justify-between items-center"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
                aria-controls={`faq-panel-${i}`}
              >
                <span className="font-medium text-gray-900 dark:text-white text-base">{faq.q}</span>
                <span className={`ml-4 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>▼</span>
              </button>
              <div
                id={`faq-panel-${i}`}
                className={`text-gray-700 dark:text-gray-300 text-sm pb-5 transition-all duration-200 ease-in-out ${openFaq === i ? 'block' : 'hidden'}`}
              >
                {faq.a}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
